import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useBrands } from "../contexts/BrandContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { ChevronLeft, ChevronRight, Plus, Trash2, Download, GripVertical, Lock, MessageSquare, Eye, Pencil, MoreVertical, Copy, Repeat, CheckCircle2, XCircle, Clock3, AlertTriangle, Globe2, LogIn, UserX, RotateCcw, BedDouble } from "lucide-react";
import TimePicker24 from "../components/TimePicker24";
import SearchableSelect from "../components/SearchableSelect";
import { formatTimezoneLabel, timezoneFull } from "../lib/timezone";
import { toast } from "sonner";
import ShiftDetailsDialog from "../components/ShiftDetailsDialog";
import DuplicateRecurrenceDialog from "../components/DuplicateRecurrenceDialog";
import ShiftConflictDialog from "../components/ShiftConflictDialog";
import HorizontalScrollContainer from "../components/HorizontalScrollContainer";
import QuickSearchBar, { useQuickFilter } from "../components/QuickSearchBar";
import StickyPageHeader from "../components/StickyPageHeader";
import NonWorkingDayBanner from "../components/NonWorkingDayBanner";
import RecurrenceFormSection, { RECURRENCE_EMPTY, toRecurrencePayload } from "../components/RecurrenceFormSection";
import { useNonWorkingDays } from "../hooks/useNonWorkingDays";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "../components/ui/dropdown-menu";

function startOfWeek(d) {
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day; // Monday as first day
  const x = new Date(d);
  x.setDate(d.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}
function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Formate la liste des pauses d'un shift en chaîne lisible.
// Ex: "Pause 12:00-13:00 (déjeuner, non rém.) · Pause 16:00-16:15 (rém.)"
function formatBreaksLabel(breaks) {
  if (!Array.isArray(breaks) || breaks.length === 0) return "";
  return breaks
    .map((b) => {
      const lbl = (b.label || "").trim();
      const tail = lbl ? ` (${lbl}${b.is_paid ? ", rém." : ", non rém."})` : (b.is_paid ? " (rém.)" : " (non rém.)");
      return `Pause ${b.start_time}-${b.end_time}${tail}`;
    })
    .join(" · ");
}


function startOfMonthGrid(d) {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  return startOfWeek(first);
}

export default function Planning() {
  const { selectedBrandId, brands } = useBrands();
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [shiftCategories, setShiftCategories] = useState([]);
  const [posteFilter, setPosteFilter] = useState("all");
  const [filters, setFilters] = useState({ q: "", brand_id: "", site_id: "", category_id: "", collaborateur_id: "" });
  const [dragOverDate, setDragOverDate] = useState(null);
  const [detailsShift, setDetailsShift] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = création
  const [editingSeries, setEditingSeries] = useState({ isSeries: false, futureCount: 0 }); // info série récurrente
  const [scopeDialog, setScopeDialog] = useState({ open: false, choice: "this", busy: false }); // choix "cette occurrence / et suivantes"
  const [dupDialog, setDupDialog] = useState({ open: false, shift: null });
  const [form, setForm] = useState({
    collaborateur_id: "",
    brand_id: selectedBrandId || "",
    site_id: "",
    shift_category_id: "",
    date: fmt(new Date()),
    start_time: "09:00",
    end_time: "17:00",
    role_label: "",
    notes: "",
    breaks: [],
  });
  const [recurrence, setRecurrence] = useState(RECURRENCE_EMPTY);

  const canEdit = user?.role === "admin" || user?.role === "manager";

  // ── Détection de conflits (temps réel + modale bloquante) ────────────────
  // Badge en haut du formulaire : appelle /shifts/check-conflicts à chaque
  // modification importante (collab/date/horaires), avec debouncing 350ms.
  const [liveConflict, setLiveConflict] = useState({ has_conflict: false, shifts: [], leaves: [] });
  const [conflictDialog, setConflictDialog] = useState({ open: false, conflict: null, pending: null, busy: false });
  const liveConflictTimer = useRef(null);

  useEffect(() => {
    if (liveConflictTimer.current) clearTimeout(liveConflictTimer.current);
    liveConflictTimer.current = setTimeout(async () => {
      if (!open || !form.collaborateur_id || !form.date || !form.start_time || !form.end_time) {
        setLiveConflict({ has_conflict: false, shifts: [], leaves: [] });
        return;
      }
      try {
        const { data } = await api.post("/shifts/check-conflicts", {
          collaborateur_id: form.collaborateur_id,
          date: form.date,
          start_time: form.start_time,
          end_time: form.end_time,
          exclude_shift_id: editingId || undefined,
        });
        setLiveConflict(data || { has_conflict: false, shifts: [], leaves: [] });
      } catch {
        // silencieux : la modale bloquante du submit reste la vraie barrière
      }
    }, 350);
    return () => liveConflictTimer.current && clearTimeout(liveConflictTimer.current);
  }, [open, form.collaborateur_id, form.date, form.start_time, form.end_time, editingId]);

  // Ouvre la modale Nouvelle planification en pré-remplissant la date (utilisé par les boutons + de chaque jour)
  const openNewShiftForDay = (isoDate) => {
    setEditingId(null);
    setForm({
      collaborateur_id: "",
      brand_id: selectedBrandId || "",
      site_id: "",
      shift_category_id: "",
      date: isoDate,
      start_time: "09:00",
      end_time: "17:00",
      role_label: "",
      notes: "",
      breaks: [],
    });
    setRecurrence(RECURRENCE_EMPTY);
    setOpen(true);
  };

  // Ouvre la modale en mode édition d'un shift existant.
  const openEditShift = async (s) => {
    setEditingId(s.id);
    setForm({
      collaborateur_id: s.collaborateur_id || "",
      brand_id: s.brand_id || "",
      site_id: s.site_id || "",
      shift_category_id: s.shift_category_id || "",
      date: s.date || fmt(new Date()),
      start_time: s.start_time || "09:00",
      end_time: s.end_time || "17:00",
      role_label: s.role_label || "",
      notes: s.notes || "",
      breaks: Array.isArray(s.breaks) ? s.breaks.map((b) => ({
        start_time: b.start_time || "12:00",
        end_time: b.end_time || "13:00",
        is_paid: !!b.is_paid,
        label: b.label || "",
      })) : [],
    });
    setEditingSeries({ isSeries: false, futureCount: 0 });
    setRecurrence(RECURRENCE_EMPTY);
    setOpen(true);
    // Récupère l'info "shift récurrent" en tâche de fond (backfill éventuel côté serveur)
    try {
      const { data } = await api.get(`/shifts/${s.id}/series-info`);
      if (data?.is_series) {
        setEditingSeries({ isSeries: true, futureCount: data.future_count || 0 });
      }
    } catch {
      // Non bloquant : si l'endpoint échoue, on retombe sur le comportement single-shift
    }
  };

  // Locks a manager's own shift if it was created by an admin
  const isLockedForManager = (s) =>
    user?.role === "manager" && s?.created_by_role === "admin" && s?.collaborateur_id === user?.id;

  // ─── Validation de shift (Admin + Manager du département) ─────────────────
  // Statuts : "pending" (Planifié · neutre), "completed" (Effectué · vert),
  // "missed" (Non effectué · rouge). Auto-marquage côté backend via pointage.
  const canValidateShift = (s) => {
    if (!s) return false;
    if (user?.role === "admin") return true;
    if (user?.role === "manager") {
      const scope = user?.brand_ids || [];
      return scope.includes(s.brand_id);
    }
    return false;
  };

  const validationStatusOf = (s) => {
    const v = (s?.validation_status || "pending").toLowerCase();
    return ["pending", "completed", "missed"].includes(v) ? v : "pending";
  };

  // Classes Tailwind appliquées sur la pastille du shift selon le statut.
  const validationCardClasses = (status) => {
    if (status === "completed") return "bg-emerald-50 border-emerald-300 hover:border-emerald-500";
    if (status === "missed") return "bg-rose-50 border-rose-300 hover:border-rose-500";
    return "bg-blue-50 border-blue-200 hover:border-blue-400";
  };

  const setShiftValidation = async (shiftId, status) => {
    try {
      const updated = await api.put(`/shifts/${shiftId}/validation`, { validation_status: status });
      setShifts((prev) => prev.map((x) => (x.id === shiftId ? { ...x, ...updated.data } : x)));
      const label = status === "completed" ? "Effectué" : status === "missed" ? "Non effectué" : "Planifié";
      toast.success(`Statut mis à jour : ${label}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de mettre à jour le statut");
    }
  };

  // ----- Shift change request dialog (manager) -----
  const [changeReqOpen, setChangeReqOpen] = useState(false);
  const [changeReqShift, setChangeReqShift] = useState(null);
  const [changeReqForm, setChangeReqForm] = useState({ action: "update", date: "", start_time: "", end_time: "", reason: "" });

  const openChangeRequest = (s) => {
    setChangeReqShift(s);
    setChangeReqForm({
      action: "update",
      date: s.date,
      start_time: s.start_time,
      end_time: s.end_time,
      reason: "",
    });
    setChangeReqOpen(true);
  };

  const submitChangeRequest = async () => {
    if (!changeReqShift) return;
    try {
      const proposed_changes = changeReqForm.action === "update"
        ? {
            date: changeReqForm.date,
            start_time: changeReqForm.start_time,
            end_time: changeReqForm.end_time,
          }
        : null;
      await api.post("/shift-change-requests", {
        shift_id: changeReqShift.id,
        action: changeReqForm.action,
        proposed_changes,
        reason: changeReqForm.reason,
      });
      toast.success("Demande envoyée à l'administrateur");
      setChangeReqOpen(false);
      setChangeReqShift(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const days = useMemo(() => {
    if (viewMode === "week") {
      const ws = startOfWeek(cursor);
      return Array.from({ length: 7 }).map((_, i) => {
        const d = new Date(ws);
        d.setDate(ws.getDate() + i);
        return d;
      });
    }
    const start = startOfMonthGrid(cursor);
    return Array.from({ length: 42 }).map((_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor, viewMode]);

  const periodLabel = useMemo(() => {
    if (viewMode === "week") {
      const fmtDisplay = (d) => `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
      return `${fmtDisplay(days[0])} → ${fmtDisplay(days[days.length - 1])}`;
    }
    return cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  }, [days, cursor, viewMode]);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      params.set("start_date", fmt(days[0]));
      params.set("end_date", fmt(days[days.length - 1]));
      if (selectedBrandId) params.set("brand_id", selectedBrandId);
      const res = await api.get(`/shifts/export.csv?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `plannings_${fmt(days[0])}_${fmt(days[days.length - 1])}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export généré");
    } catch (e) {
      toast.error("Erreur d'export");
    }
  };

  // ---- Drag & Drop : move shift to a different date ----
  const handleDragStart = (e, shift) => {
    if (!canEdit) return;
    if (isLockedForManager(shift)) {
      e.preventDefault();
      toast.info("Ce shift a été créé par un admin — utilisez 'Demander une modification'.");
      return;
    }
    e.dataTransfer.setData("text/plain", shift.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e, ds) => {
    if (!canEdit) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverDate !== ds) setDragOverDate(ds);
  };
  const handleDragLeave = () => setDragOverDate(null);
  const handleDrop = async (e, ds) => {
    if (!canEdit) return;
    e.preventDefault();
    setDragOverDate(null);
    const id = e.dataTransfer.getData("text/plain");
    const sh = shifts.find((s) => s.id === id);
    if (!sh || sh.date === ds) return;
    // optimistic update
    setShifts((list) => list.map((s) => (s.id === id ? { ...s, date: ds } : s)));
    try {
      await api.put(`/shifts/${id}`, { date: ds });
      toast.success("Shift déplacé");
      window.dispatchEvent(new CustomEvent("plania:shifts-updated"));
    } catch (err) {
      // Restore visuel optimiste
      setShifts((list) => list.map((s) => (s.id === id ? { ...s, date: sh.date } : s)));
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object" && detail.code === "shift_conflict") {
        // Ouvre la modale bloquante : confirmation pour appliquer quand même
        setConflictDialog({
          open: true,
          conflict: detail,
          pending: { kind: "drag", shiftId: id, originalDate: sh.date, targetDate: ds },
          busy: false,
        });
        return;
      }
      toast.error(err.response?.data?.detail || "Erreur de déplacement");
    }
  };

  const load = async () => {
    try {
      const start = fmt(days[0]);
      const end = fmt(days[days.length - 1]);
      const params = { start_date: start, end_date: end };
      if (selectedBrandId) params.brand_id = selectedBrandId;
      const [sRes, uRes, siteRes, catRes] = await Promise.all([
        api.get("/shifts", { params }),
        api.get("/collaborateurs", { params: selectedBrandId ? { brand_id: selectedBrandId } : {} }),
        api.get("/sites", { params: selectedBrandId ? { brand_id: selectedBrandId } : {} }),
        api.get("/shift-categories", { params: selectedBrandId ? { brand_id: selectedBrandId } : {} }),
      ]);
      setShifts(sRes.data || []);
      setUsers(uRes.data || []);
      setSites(siteRes.data || []);
      setShiftCategories(catRes.data || []);
    } catch {
      // ignore
    }
  };

  useEffect(() => { load(); }, [cursor, viewMode, selectedBrandId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Jours non travaillés couvrant la plage affichée
  const { byDate: nwdByDate } = useNonWorkingDays({
    startDate: fmt(days[0]),
    endDate: fmt(days[days.length - 1]),
    brandId: selectedBrandId || undefined,
  });

  const doSaveShift = async (force = false, scope = "this") => {
    const payload = {
      ...form,
      site_id: form.site_id || null,
      shift_category_id: form.shift_category_id || null,
      breaks: (form.breaks || []).map((b) => ({
        start_time: b.start_time,
        end_time: b.end_time,
        is_paid: !!b.is_paid,
        label: (b.label || "").trim() || null,
      })),
    };
    const params = new URLSearchParams();
    if (force) params.set("force", "true");
    if (editingId && scope === "future") params.set("scope", "future");
    const qs = params.toString() ? `?${params.toString()}` : "";
    let sourceShiftId = editingId;
    if (editingId) {
      const { data } = await api.put(`/shifts/${editingId}${qs}`, payload);
      if (scope === "future" && data?.series_updated_count) {
        toast.success(`${data.series_updated_count} shift(s) de la série mis à jour${force ? " (conflit ignoré)" : ""}`);
      } else {
        toast.success(force ? "Shift mis à jour (conflit ignoré)" : "Shift mis à jour");
      }
    } else {
      const { data } = await api.post(`/shifts${qs}`, payload);
      sourceShiftId = data?.id || null;
      toast.success(force ? "Shift créé (conflit ignoré)" : "Shift créé");
    }
    // Récurrence : crée les occurrences supplémentaires à partir du shift source
    const recPayload = toRecurrencePayload(recurrence);
    if (recPayload && sourceShiftId) {
      try {
        const { data } = await api.post(`/shifts/${sourceShiftId}/recurrence`, recPayload);
        if (data?.created > 0) {
          toast.success(`${data.created} occurrence(s) récurrente(s) créée(s)`);
        } else {
          toast.info("Aucune occurrence supplémentaire à créer (période ou jours non couverts)");
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Récurrence non appliquée");
      }
    }
    setOpen(false);
    setEditingId(null);
    setEditingSeries({ isSeries: false, futureCount: 0 });
    setRecurrence(RECURRENCE_EMPTY);
    setScopeDialog({ open: false, choice: "this", busy: false });
    setConflictDialog({ open: false, conflict: null, pending: null, busy: false });
    load();
    window.dispatchEvent(new CustomEvent("plania:shifts-updated"));
  };

  const submit = async () => {
    try {
      if (!form.collaborateur_id || !form.brand_id) {
        toast.error("Veuillez sélectionner un collaborateur et un département");
        return;
      }
      // Validation de la récurrence
      if (recurrence.recurrence_type !== "once") {
        if (!recurrence.end_date) {
          toast.error("Date de fin requise pour une récurrence");
          return;
        }
        if (recurrence.end_date <= form.date) {
          toast.error("La date de fin de récurrence doit être postérieure à la date du shift");
          return;
        }
      }
      // Si édition d'un shift récurrent avec au moins 1 occurrence future → demander la portée
      if (editingId && editingSeries.isSeries && editingSeries.futureCount > 0) {
        setScopeDialog({ open: true, choice: "this", busy: false });
        return;
      }
      await doSaveShift(false, "this");
    } catch (e) {
      // Intercepte 409 (conflit) → ouvrir la modale bloquante
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail && typeof detail === "object" && detail.code === "shift_conflict") {
        setConflictDialog({
          open: true,
          conflict: detail,
          pending: { kind: "save" },
          busy: false,
        });
        return;
      }
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // Confirmation du choix de portée pour l'édition d'un shift récurrent
  const confirmScopeChoice = async () => {
    const scope = scopeDialog.choice === "future" ? "future" : "this";
    setScopeDialog((d) => ({ ...d, busy: true }));
    try {
      await doSaveShift(false, scope);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setScopeDialog((d) => ({ ...d, busy: false }));
      if (status === 409 && detail && typeof detail === "object" && detail.code === "shift_conflict") {
        // On mémorise la portée pour un éventuel "force" dans la modale de conflit
        setConflictDialog({
          open: true,
          conflict: detail,
          pending: { kind: "save", scope },
          busy: false,
        });
        setScopeDialog({ open: false, choice: "this", busy: false });
        return;
      }
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // Confirmation bypass : on relance la requête avec force=true selon l'action en cours
  const confirmConflictBypass = async () => {
    const pending = conflictDialog.pending;
    if (!pending) {
      setConflictDialog({ open: false, conflict: null, pending: null, busy: false });
      return;
    }
    setConflictDialog((d) => ({ ...d, busy: true }));
    try {
      if (pending.kind === "save") {
        await doSaveShift(true, pending.scope || "this");
      } else if (pending.kind === "drag") {
        await api.put(`/shifts/${pending.shiftId}?force=true`, { date: pending.targetDate });
        setShifts((list) => list.map((s) => (s.id === pending.shiftId ? { ...s, date: pending.targetDate } : s)));
        toast.success("Shift déplacé (conflit ignoré)");
        window.dispatchEvent(new CustomEvent("plania:shifts-updated"));
        setConflictDialog({ open: false, conflict: null, pending: null, busy: false });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la confirmation");
      setConflictDialog({ open: false, conflict: null, pending: null, busy: false });
    }
  };

  const cancelConflictDialog = () => {
    setConflictDialog({ open: false, conflict: null, pending: null, busy: false });
  };

  // Bouton "Voir la fiche" depuis la modale de conflit : récupère le shift complet
  // et ouvre le ShiftDetailsDialog. La modale de conflit reste affichée derrière.
  const viewConflictShift = async (conflictShift) => {
    if (!conflictShift?.id) return;
    try {
      const { data } = await api.get(`/shifts/${conflictShift.id}`);
      setDetailsShift(data);
      setDetailsOpen(true);
    } catch (e) {
      // fallback : ouvrir avec les infos partielles du payload de conflit
      setDetailsShift(conflictShift);
      setDetailsOpen(true);
    }
  };

  // Available postes for the filter dropdown
  const postes = useMemo(() => {
    const set = new Set();
    users.forEach((u) => { if (u.poste) set.add(u.poste); });
    return Array.from(set).sort();
  }, [users]);

  // Filtered shifts based on poste filter + recherche rapide
  const visibleShifts = useMemo(() => {
    let base = shifts;
    if (posteFilter !== "all") {
      const ids = new Set(users.filter((u) => u.poste === posteFilter).map((u) => u.id));
      base = base.filter((s) => ids.has(s.collaborateur_id));
    }
    return base;
  }, [shifts, posteFilter, users]);

  const filteredShifts = useQuickFilter(visibleShifts, filters, {
    text: (s) => {
      const u = users.find((x) => x.id === s.collaborateur_id);
      const site = sites.find((x) => x.id === s.site_id);
      const cat = shiftCategories.find((x) => x.id === s.category_id);
      return `${s.title || ""} ${s.description || ""} ${u?.name || ""} ${site?.name || ""} ${cat?.name || ""}`;
    },
    predicates: {
      brand_id: (s, value) => s.brand_id === value,
      site_id: (s, value) => s.site_id === value,
      category_id: (s, value) => s.category_id === value,
      collaborateur_id: (s, value) => s.collaborateur_id === value,
    },
  });

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce shift ?")) return;
    await api.delete(`/shifts/${id}`);
    toast.success("Shift supprimé");
    load();
    window.dispatchEvent(new CustomEvent("plania:shifts-updated"));
  };

  // Actions rapides sur un shift planifié (pointage manuel / absence) sans passer
  // par la page Pointage. Les endpoints backend enforcent :
  //   - action limitée aux shifts du jour (fuseau serveur/département)
  //   - admin / manager du département uniquement
  //   - pas de double pointage, pas de double absence
  const manualClockIn = async (shiftId) => {
    try {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, "0");
      const mm = String(now.getMinutes()).padStart(2, "0");
      const clockedTime = `${hh}:${mm}`;
      await api.post(`/pointage/late-arrivals/${shiftId}/clock-in`, { clock_in_time: clockedTime });
      toast.success("Pointage validé");
      load();
      window.dispatchEvent(new CustomEvent("plania:shifts-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du pointage");
    }
  };

  const markAbsent = async (shiftId) => {
    if (!window.confirm("Marquer ce collaborateur comme absent sur ce shift ? Les interventions du département qui chevauchent seront ouvertes à remplacement.")) return;
    try {
      await api.post(`/pointage/late-arrivals/${shiftId}/mark-absent`);
      toast.success("Absence enregistrée");
      load();
      window.dispatchEvent(new CustomEvent("plania:shifts-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const unmarkAbsent = async (shiftId) => {
    if (!window.confirm("Annuler l'absence déclarée pour ce shift ?")) return;
    try {
      await api.delete(`/pointage/late-arrivals/${shiftId}/mark-absent`);
      toast.success("Absence annulée");
      load();
      window.dispatchEvent(new CustomEvent("plania:shifts-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const markUnavailable = async (shiftId) => {
    if (!window.confirm("Marquer ce collaborateur comme indisponible sur ce shift ? Une entrée sera ajoutée à son historique des indisponibilités et les interventions du département qui chevauchent seront ouvertes à remplacement.")) return;
    try {
      await api.post(`/pointage/late-arrivals/${shiftId}/mark-unavailable`);
      toast.success("Indisponibilité enregistrée");
      load();
      window.dispatchEvent(new CustomEvent("plania:shifts-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const unmarkUnavailable = async (shiftId) => {
    if (!window.confirm("Annuler l'indisponibilité déclarée pour ce shift ?")) return;
    try {
      await api.delete(`/pointage/late-arrivals/${shiftId}/mark-unavailable`);
      toast.success("Indisponibilité annulée");
      load();
      window.dispatchEvent(new CustomEvent("plania:shifts-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // Retourne true si le shift est daté d'aujourd'hui (comparaison locale sur
  // la date ISO). Les endpoints backend valident aussi via le fuseau du dépt.
  const isTodayShift = (s) => {
    if (!s?.date) return false;
    const d = new Date();
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return s.date === iso;
  };

  // Pastille de statut de validation + mini-menu contextuel (Admin + Manager du département).
  // Statuts : pending (neutre), completed (vert), missed (rouge). Si le shift est marqué
  // comme "absent" (via mark-absent), on affiche une pastille dédiée en priorité.
  const renderValidationControl = (s, { compact = false } = {}) => {
    const status = validationStatusOf(s);
    const allowed = canValidateShift(s);
    const isAbsent = s?.absence_status === "absent";
    const isUnavailable = s?.unavailability_status === "unavailable";
    const hasClockIn = !!(s?.clock_in_time || s?.clocked_in_at || s?.pointage_clock_in);
    const today = isTodayShift(s);
    const badgeBase = "inline-flex items-center gap-1 rounded-sm font-semibold uppercase tracking-wider";
    const badgeSize = compact ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]";
    let effectiveStatus = status;
    if (isAbsent) effectiveStatus = "absent";
    else if (isUnavailable) effectiveStatus = "unavailable";
    const styles = {
      completed: "bg-emerald-100 text-emerald-700 border border-emerald-300",
      missed: "bg-rose-100 text-rose-700 border border-rose-300",
      pending: "bg-zinc-100 text-zinc-600 border border-zinc-300",
      absent: "bg-red-600 text-white border border-red-700",
      unavailable: "bg-amber-500 text-white border border-amber-600",
    }[effectiveStatus];
    const Icon = effectiveStatus === "completed" ? CheckCircle2
      : effectiveStatus === "missed" ? XCircle
      : effectiveStatus === "absent" ? UserX
      : effectiveStatus === "unavailable" ? BedDouble
      : Clock3;
    const label = effectiveStatus === "completed" ? "Effectué"
      : effectiveStatus === "missed" ? "Non effectué"
      : effectiveStatus === "absent" ? "Absent"
      : effectiveStatus === "unavailable" ? "Indisponible"
      : "Planifié";
    const iconSize = compact ? 9 : 10;

    if (!allowed) {
      return (
        <span
          className={`${badgeBase} ${badgeSize} ${styles}`}
          title={`Statut : ${label}`}
          data-testid={`shift-validation-badge-${s.id}`}
        >
          <Icon size={iconSize} /> {label}
        </span>
      );
    }
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(ev) => ev.stopPropagation()}
            className={`${badgeBase} ${badgeSize} ${styles} hover:opacity-80 cursor-pointer`}
            aria-label={`Changer le statut (actuel : ${label})`}
            data-testid={`shift-validation-trigger-${s.id}`}
          >
            <Icon size={iconSize} /> {label}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">Statut de validation</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => setShiftValidation(s.id, "completed")}
            data-testid={`shift-validation-set-completed-${s.id}`}
          >
            <CheckCircle2 size={12} className="mr-2 text-emerald-600" /> Effectué
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setShiftValidation(s.id, "missed")}
            data-testid={`shift-validation-set-missed-${s.id}`}
          >
            <XCircle size={12} className="mr-2 text-rose-600" /> Non effectué
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setShiftValidation(s.id, "pending")}
            data-testid={`shift-validation-set-pending-${s.id}`}
          >
            <Clock3 size={12} className="mr-2 text-zinc-500" /> Planifié
          </DropdownMenuItem>

          {today && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">Pointage du jour</DropdownMenuLabel>
              {!isAbsent && !hasClockIn && (
                <DropdownMenuItem
                  onClick={() => manualClockIn(s.id)}
                  data-testid={`shift-clock-in-${s.id}`}
                >
                  <LogIn size={12} className="mr-2 text-emerald-600" /> Valider le pointage
                </DropdownMenuItem>
              )}
              {hasClockIn && !isAbsent && (
                <DropdownMenuItem disabled data-testid={`shift-clock-in-done-${s.id}`}>
                  <CheckCircle2 size={12} className="mr-2 text-emerald-600" /> Pointage validé
                </DropdownMenuItem>
              )}
              {!isAbsent ? (
                <DropdownMenuItem
                  onClick={() => markAbsent(s.id)}
                  data-testid={`shift-mark-absent-${s.id}`}
                  className="text-red-700 focus:text-red-800"
                >
                  <UserX size={12} className="mr-2 text-red-600" /> Marquer en absence
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => unmarkAbsent(s.id)}
                  data-testid={`shift-unmark-absent-${s.id}`}
                >
                  <RotateCcw size={12} className="mr-2 text-zinc-600" /> Annuler l&apos;absence
                </DropdownMenuItem>
              )}
              {!isAbsent && s?.unavailability_status !== "unavailable" && (
                <DropdownMenuItem
                  onClick={() => markUnavailable(s.id)}
                  data-testid={`shift-mark-unavailable-${s.id}`}
                  className="text-amber-700 focus:text-amber-800"
                >
                  <BedDouble size={12} className="mr-2 text-amber-600" /> Marquer en indisponibilité
                </DropdownMenuItem>
              )}
              {s?.unavailability_status === "unavailable" && (
                <DropdownMenuItem
                  onClick={() => unmarkUnavailable(s.id)}
                  data-testid={`shift-unmark-unavailable-${s.id}`}
                >
                  <RotateCcw size={12} className="mr-2 text-zinc-600" /> Annuler l&apos;indisponibilité
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="space-y-6" data-testid="planning-page">
      {/* Modale bloquante : conflit horaire / indisponibilité */}
      <ShiftConflictDialog
        open={conflictDialog.open}
        onOpenChange={(v) => { if (!v) cancelConflictDialog(); }}
        conflict={conflictDialog.conflict}
        onCancel={cancelConflictDialog}
        onConfirm={confirmConflictBypass}
        busy={conflictDialog.busy}
        onViewShift={viewConflictShift}
      />
      {/* Modale de portée d'édition pour un shift récurrent */}
      <Dialog open={scopeDialog.open} onOpenChange={(v) => { if (!v && !scopeDialog.busy) setScopeDialog({ open: false, choice: "this", busy: false }); }}>
        <DialogContent className="max-w-md p-0" data-testid="scope-choice-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-100">
            <DialogTitle>Shift récurrent</DialogTitle>
          </DialogHeader>
          <div className="px-6 py-4 space-y-3">
            <p className="text-xs text-zinc-600 font-mono">
              Ce shift fait partie d&apos;une série récurrente ({editingSeries.futureCount} occurrence{editingSeries.futureCount > 1 ? "s" : ""} future{editingSeries.futureCount > 1 ? "s" : ""}). Où appliquer la modification ?
            </p>
            <label className="flex items-start gap-3 p-3 rounded-sm border border-zinc-200 hover:bg-zinc-50 cursor-pointer" data-testid="scope-choice-this">
              <input
                type="radio"
                name="scope-choice"
                value="this"
                checked={scopeDialog.choice === "this"}
                onChange={() => setScopeDialog((d) => ({ ...d, choice: "this" }))}
                className="mt-0.5 accent-[#0055FF]"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-zinc-900">Cette occurrence uniquement</p>
                <p className="text-[11px] text-zinc-500 font-mono">Seul ce shift du {form.date} sera modifié.</p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-sm border border-zinc-200 hover:bg-zinc-50 cursor-pointer" data-testid="scope-choice-future">
              <input
                type="radio"
                name="scope-choice"
                value="future"
                checked={scopeDialog.choice === "future"}
                onChange={() => setScopeDialog((d) => ({ ...d, choice: "future" }))}
                className="mt-0.5 accent-[#0055FF]"
              />
              <div className="flex-1">
                <p className="text-sm font-semibold text-zinc-900">Cette occurrence et les suivantes</p>
                <p className="text-[11px] text-zinc-500 font-mono">
                  Applique horaires, collaborateur, département, site, catégorie, poste, notes et pauses à toutes les occurrences ≥ {form.date}. Les dates individuelles restent inchangées.
                </p>
              </div>
            </label>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
            <Button
              variant="outline"
              onClick={() => setScopeDialog({ open: false, choice: "this", busy: false })}
              disabled={scopeDialog.busy}
              data-testid="scope-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmScopeChoice}
              disabled={scopeDialog.busy}
              className="bg-[#0055FF] hover:bg-[#0044CC]"
              data-testid="scope-confirm-btn"
            >
              {scopeDialog.busy ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Manager change request dialog */}
      <Dialog open={changeReqOpen} onOpenChange={setChangeReqOpen}>
        <DialogContent className="flex flex-col max-h-[90vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
            <DialogTitle>Demander une modification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1" data-testid="change-req-dialog-scroll">
            <p className="text-xs text-zinc-600 font-mono">
              Ce shift a été créé par un administrateur. Décrivez la modification souhaitée — un admin validera votre demande.
            </p>
            <div>
              <Label className="label-uppercase mb-1.5 block">Type de demande</Label>
              <Select value={changeReqForm.action} onValueChange={(v) => setChangeReqForm({ ...changeReqForm, action: v })}>
                <SelectTrigger data-testid="change-req-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="update">Modifier les horaires</SelectItem>
                  <SelectItem value="delete">Supprimer le shift</SelectItem>
                  <SelectItem value="leave">Déclarer une indisponibilité</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {changeReqForm.action === "update" && (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="label-uppercase mb-1.5 block">Date</Label>
                  <Input type="date" value={changeReqForm.date} onChange={(e) => setChangeReqForm({ ...changeReqForm, date: e.target.value })} data-testid="change-req-date" />
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Début</Label>
                  <TimePicker24 value={changeReqForm.start_time} onChange={(v) => setChangeReqForm({ ...changeReqForm, start_time: v })} data-testid="change-req-start" />
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Fin</Label>
                  <TimePicker24 value={changeReqForm.end_time} onChange={(v) => setChangeReqForm({ ...changeReqForm, end_time: v })} data-testid="change-req-end" />
                </div>
              </div>
            )}
            <div>
              <Label className="label-uppercase mb-1.5 block">Motif</Label>
              <Textarea rows={3} value={changeReqForm.reason} onChange={(e) => setChangeReqForm({ ...changeReqForm, reason: e.target.value })} placeholder="Expliquez la raison de votre demande…" data-testid="change-req-reason" />
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
            <Button variant="outline" onClick={() => setChangeReqOpen(false)}>Annuler</Button>
            <Button onClick={submitChangeRequest} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="change-req-submit-btn">Envoyer la demande</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StickyPageHeader testid="planning-sticky-header">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-uppercase mb-2">Planning</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900">
            {viewMode === "week" ? "Planification hebdomadaire" : "Planification mensuelle"}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex border border-zinc-200 rounded-sm overflow-hidden" data-testid="view-toggle">
            <button
              onClick={() => setViewMode("week")}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${viewMode === "week" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
              data-testid="view-week-btn"
            >
              Semaine
            </button>
            <button
              onClick={() => setViewMode("month")}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-l border-zinc-200 ${viewMode === "month" ? "bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
              data-testid="view-month-btn"
            >
              Mois
            </button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const d = new Date(cursor);
              if (viewMode === "week") d.setDate(d.getDate() - 7);
              else d.setMonth(d.getMonth() - 1);
              setCursor(d);
            }}
            data-testid="prev-period-btn"
            className="rounded-sm"
          >
            <ChevronLeft size={14} />
          </Button>
          <span className="font-mono text-xs text-zinc-600 px-3 min-w-[180px] text-center capitalize" data-testid="period-label">
            {periodLabel}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const d = new Date(cursor);
              if (viewMode === "week") d.setDate(d.getDate() + 7);
              else d.setMonth(d.getMonth() + 1);
              setCursor(d);
            }}
            data-testid="next-period-btn"
            className="rounded-sm"
          >
            <ChevronRight size={14} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            className="rounded-sm"
            data-testid="export-shifts-csv"
          >
            <Download size={14} className="mr-1" /> CSV
          </Button>
          {canEdit && (
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setEditingSeries({ isSeries: false, futureCount: 0 }); setRecurrence(RECURRENCE_EMPTY); } }}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditingId(null); setEditingSeries({ isSeries: false, futureCount: 0 }); setRecurrence(RECURRENCE_EMPTY); }} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm" data-testid="new-shift-btn">
                  <Plus size={14} className="mr-1" /> Nouvelle planification
                </Button>
              </DialogTrigger>
              <DialogContent className="flex flex-col max-h-[90vh] p-0">
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
                  <DialogTitle>{editingId ? "Modifier le shift" : "Nouvelle planification"}</DialogTitle>
                  {editingId && editingSeries.isSeries && editingSeries.futureCount > 0 && (
                    <p className="text-[11px] font-mono text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-sm px-2 py-1 mt-2 w-fit" data-testid="shift-series-hint">
                      Shift récurrent · {editingSeries.futureCount} occurrence{editingSeries.futureCount > 1 ? "s" : ""} future{editingSeries.futureCount > 1 ? "s" : ""}
                    </p>
                  )}
                </DialogHeader>
                <div className="space-y-4 overflow-y-auto px-6 py-4 flex-1" data-testid="shift-dialog-scroll">
                  {/* Badge temps réel : alerte si conflit horaire ou indisponibilité */}
                  {liveConflict.has_conflict && (
                    <div
                      className="flex items-start gap-2 p-3 rounded-sm border border-amber-300 bg-amber-50 text-amber-900"
                      data-testid="shift-form-conflict-badge"
                      role="alert"
                    >
                      <AlertTriangle size={16} className="text-amber-700 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 text-xs">
                        <p className="font-semibold mb-0.5">
                          Conflit détecté pour ce collaborateur
                        </p>
                        <p className="text-amber-800 font-mono text-[11px]">
                          {liveConflict.shifts.length > 0 && (
                            <>
                              {liveConflict.shifts.length} shift{liveConflict.shifts.length > 1 ? "s" : ""} qui se chevauche{liveConflict.shifts.length > 1 ? "nt" : ""}
                              {liveConflict.leaves.length > 0 ? " · " : ""}
                            </>
                          )}
                          {liveConflict.leaves.length > 0 && (
                            <>
                              {liveConflict.leaves.length} indisponibilité{liveConflict.leaves.length > 1 ? "s" : ""}
                            </>
                          )}
                        </p>
                        <p className="text-amber-700 text-[11px] mt-1">
                          Le système vous demandera confirmation lors de l&apos;enregistrement.
                        </p>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Collaborateur</Label>
                    <SearchableSelect
                      value={form.collaborateur_id}
                      onChange={(v) => {
                        const u = users.find((x) => x.id === v);
                        setForm((f) => ({
                          ...f,
                          collaborateur_id: v,
                          role_label: u?.poste || f.role_label,
                        }));
                      }}
                      options={users.map((u) => ({
                        value: u.id,
                        label: u.name + (u.poste ? ` — ${u.poste}` : ""),
                        keywords: `${u.name || ""} ${u.poste || ""} ${u.email || ""} ${u.matricule || ""}`,
                      }))}
                      placeholder="Sélectionner un collaborateur…"
                      searchPlaceholder="Nom, poste, email, matricule…"
                      emptyLabel="Aucun collaborateur trouvé."
                      data-testid="shift-collab-select"
                    />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Département</Label>
                    <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v, site_id: "" })}>
                      <SelectTrigger data-testid="shift-brand-select"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                      <SelectContent>
                        {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Site (optionnel)</Label>
                    <SearchableSelect
                      value={form.site_id || ""}
                      onChange={(v) => setForm({ ...form, site_id: v })}
                      options={sites
                        .filter((s) => !form.brand_id || !s.brand_id || s.brand_id === form.brand_id)
                        .map((s) => ({
                          value: s.id,
                          label: s.name,
                          keywords: `${s.name || ""} ${s.city || ""} ${s.address || ""} ${s.code || ""}`,
                          hint: s.city || undefined,
                        }))}
                      placeholder="Aucun"
                      searchPlaceholder="Nom, ville, adresse…"
                      emptyLabel="Aucun site trouvé."
                      allowClear
                      data-testid="shift-site-select"
                    />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Catégorie de shift</Label>
                    <Select value={form.shift_category_id || "none"} onValueChange={(v) => setForm({ ...form, shift_category_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="shift-category-select"><SelectValue placeholder="Aucune" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucune</SelectItem>
                        {shiftCategories.filter((c) => {
                          if (!form.brand_id) return true;
                          const ids = (c.brand_ids && c.brand_ids.length > 0) ? c.brand_ids : (c.brand_id ? [c.brand_id] : []);
                          return ids.includes(form.brand_id);
                        }).map((c) => (
                          <SelectItem key={c.id} value={c.id} data-testid={`shift-cat-opt-${c.id}`}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Date</Label>
                      <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="shift-date" />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Début</Label>
                      <TimePicker24 value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} data-testid="shift-start" />
                    </div>
                    <div>
                      <Label className="label-uppercase mb-1.5 block">Fin</Label>
                      <TimePicker24 value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} data-testid="shift-end" />
                    </div>
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Poste / Rôle</Label>
                    <Input value={form.role_label} onChange={(e) => setForm({ ...form, role_label: e.target.value })} placeholder="Ex: Serveur, Caissier…" data-testid="shift-role" />
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">Notes</Label>
                    <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                  </div>

                  {/* Pauses dans le shift */}
                  <div className="space-y-2 pt-2 border-t border-zinc-100">
                    <div className="flex items-center justify-between">
                      <Label className="label-uppercase">Pauses</Label>
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => setForm({ ...form, breaks: [...(form.breaks || []), { start_time: "12:00", end_time: "13:00", is_paid: false, label: "" }] })}
                        data-testid="shift-add-break-btn"
                        className="h-7 px-2 text-xs"
                      >
                        + Ajouter une pause
                      </Button>
                    </div>
                    {(form.breaks || []).length === 0 && (
                      <p className="text-[11px] text-zinc-500 font-mono italic">Aucune pause planifiée.</p>
                    )}
                    {(form.breaks || []).map((br, idx) => (
                      <div
                        key={idx}
                        className="grid grid-cols-12 gap-2 items-end p-2 border border-zinc-200 rounded-sm bg-zinc-50"
                        data-testid={`shift-break-row-${idx}`}
                      >
                        <div className="col-span-3">
                          <Label className="text-[10px] font-mono text-zinc-500 mb-1 block">Début</Label>
                          <TimePicker24
                            value={br.start_time}
                            onChange={(v) => {
                              const next = [...form.breaks];
                              next[idx] = { ...next[idx], start_time: v };
                              setForm({ ...form, breaks: next });
                            }}
                            data-testid={`shift-break-start-${idx}`}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-[10px] font-mono text-zinc-500 mb-1 block">Fin</Label>
                          <TimePicker24
                            value={br.end_time}
                            onChange={(v) => {
                              const next = [...form.breaks];
                              next[idx] = { ...next[idx], end_time: v };
                              setForm({ ...form, breaks: next });
                            }}
                            data-testid={`shift-break-end-${idx}`}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-[10px] font-mono text-zinc-500 mb-1 block">Libellé</Label>
                          <Input
                            value={br.label || ""}
                            onChange={(e) => {
                              const next = [...form.breaks];
                              next[idx] = { ...next[idx], label: e.target.value };
                              setForm({ ...form, breaks: next });
                            }}
                            placeholder="Déjeuner…"
                            data-testid={`shift-break-label-${idx}`}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div className="col-span-2 flex items-center gap-1.5 pb-1">
                          <input
                            type="checkbox" checked={!!br.is_paid}
                            onChange={(e) => {
                              const next = [...form.breaks];
                              next[idx] = { ...next[idx], is_paid: e.target.checked };
                              setForm({ ...form, breaks: next });
                            }}
                            data-testid={`shift-break-paid-${idx}`}
                            className="accent-[#0055FF]"
                          />
                          <span className="text-[11px] text-zinc-700">Rémunérée</span>
                        </div>
                        <div className="col-span-1 flex justify-end pb-1">
                          <Button
                            type="button" variant="outline" size="sm"
                            onClick={() => setForm({ ...form, breaks: form.breaks.filter((_, i) => i !== idx) })}
                            data-testid={`shift-break-remove-${idx}`}
                            className="h-7 w-7 p-0 text-red-700 border-red-200 hover:bg-red-50"
                            title="Retirer cette pause"
                          >
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {(form.breaks || []).length > 0 && (
                      <p className="text-[11px] text-zinc-500 font-mono">
                        Les pauses non rémunérées sont déduites des heures effectives.
                      </p>
                    )}
                  </div>

                  <RecurrenceFormSection
                    value={recurrence}
                    onChange={setRecurrence}
                    startDate={form.date}
                    testIdPrefix="shift-rec"
                  />
                </div>
                <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
                  <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button onClick={submit} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="shift-save-btn">Enregistrer</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Recherche rapide + filtres */}
      <QuickSearchBar
        value={filters}
        onChange={setFilters}
        placeholder="Rechercher un shift (titre, description, collaborateur, site, catégorie)…"
        testid="planning-search"
        filters={[
          { key: "brand_id", label: "Département", type: "select", placeholder: "Tous les départements", options: brands.map((b) => ({ value: b.id, label: b.name })) },
          { key: "site_id", label: "Site", type: "select", placeholder: "Tous les sites", options: sites.map((s) => ({ value: s.id, label: s.name })) },
          { key: "category_id", label: "Catégorie", type: "select", placeholder: "Toutes les catégories", options: shiftCategories.map((c) => ({ value: c.id, label: c.name })) },
          { key: "collaborateur_id", label: "Collaborateur", type: "select", placeholder: "Tous les collaborateurs", options: users.map((u) => ({ value: u.id, label: u.name })) },
        ]}
      />

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="label-uppercase text-xs">Filtrer par poste</Label>
        <Select value={posteFilter} onValueChange={setPosteFilter}>
          <SelectTrigger className="w-56" data-testid="poste-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="poste-filter-all">Tous les postes</SelectItem>
            {postes.map((p) => <SelectItem key={p} value={p} data-testid={`poste-filter-${p}`}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        {posteFilter !== "all" && (
          <button onClick={() => setPosteFilter("all")} className="text-[11px] text-zinc-500 hover:text-zinc-900 font-mono underline" data-testid="poste-filter-clear">
            réinitialiser
          </button>
        )}
      </div>
      </StickyPageHeader>

      {/* Calendar grid */}
      {viewMode === "week" ? (
        <div className="space-y-3" data-testid="planning-week-bands">
          {days.map((d, i) => {
            const ds = fmt(d);
            const dayShifts = filteredShifts.filter((s) => s.date === ds);
            const isToday = ds === fmt(new Date());
            const isDragOver = dragOverDate === ds;
            const nwdEntries = nwdByDate[ds] || [];
            const isNonWorking = nwdEntries.length > 0;
            return (
              <div
                key={i}
                onDragOver={(e) => handleDragOver(e, ds)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, ds)}
                className={`bg-white border rounded-sm overflow-hidden ${isNonWorking ? "border-red-300" : isToday ? "border-[#0055FF]" : "border-zinc-200"} ${isDragOver && canEdit ? "ring-2 ring-blue-300" : ""}`}
                data-testid={`day-cell-${ds}`}
              >
                {isNonWorking && (
                  <NonWorkingDayBanner
                    entries={nwdEntries}
                    showBrands={!selectedBrandId}
                    brands={brands}
                    testIdPrefix="planning-nwd"
                  />
                )}
                {/* Bandeau jour */}
                <div className="px-4 py-2.5 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/60">
                  <div className="flex items-center gap-3">
                    <div className="text-center min-w-[44px]">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{d.toLocaleDateString("fr-FR", { weekday: "short" })}</p>
                      <p className="text-xl font-display font-bold tracking-tight text-zinc-900">{d.getDate()}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-zinc-900">{d.toLocaleDateString("fr-FR", { month: "long" })}</p>
                      <p className="text-[11px] text-zinc-500">{dayShifts.length} {dayShifts.length > 1 ? "interventions" : "intervention"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isToday && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-[#0055FF] text-white rounded-sm">Aujourd&apos;hui</span>
                    )}
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => openNewShiftForDay(ds)}
                        aria-label={`Créer un shift le ${ds}`}
                        title="Créer un shift ce jour"
                        data-testid={`day-add-shift-${ds}`}
                        className="h-7 w-7 flex items-center justify-center rounded-sm border border-zinc-200 text-zinc-600 hover:bg-[#0055FF] hover:text-white hover:border-[#0055FF] transition-colors"
                      >
                        <Plus size={14} />
                      </button>
                    )}
                  </div>
                </div>
                {/* Shifts du jour */}
                <div className="p-3">
                  {dayShifts.length === 0 ? (
                    <p className="text-xs text-zinc-400 text-center py-6">Aucun shift planifié</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                      {dayShifts.map((s) => {
                        const u = users.find((x) => x.id === s.collaborateur_id);
                        const b = brands.find((x) => x.id === s.brand_id);
                        const site = sites.find((x) => x.id === s.site_id);
                        const cat = shiftCategories.find((x) => x.id === s.shift_category_id);
                        const locked = isLockedForManager(s);
                        return (
                          <div
                            key={s.id}
                            draggable={canEdit && !locked}
                            onDragStart={(e) => handleDragStart(e, s)}
                            className={`group p-2.5 border rounded-sm ${validationCardClasses(validationStatusOf(s))} ${canEdit && !locked ? "cursor-grab active:cursor-grabbing" : "cursor-default"}`}
                            style={{
                              borderLeftWidth: 3,
                              borderLeftColor: cat?.color || b?.color || "#0055FF",
                            }}
                            data-testid={`planning-shift-${s.id}`}
                          >
                            <div className="flex items-start justify-between gap-1">
                              <p className="text-xs font-semibold text-blue-900">{s.start_time}–{s.end_time}</p>
                              {canEdit && !locked && <GripVertical size={11} className="text-zinc-300 mt-0.5" />}
                              {locked && <Lock size={11} className="text-amber-600 mt-0.5" data-testid={`shift-locked-${s.id}`} />}
                            </div>
                            {b?.timezone && (() => {
                              const tz = formatTimezoneLabel(b.timezone);
                              return tz.full ? (
                                <p
                                  className="mt-0.5 text-[10px] text-zinc-500 font-mono truncate flex items-center gap-1"
                                  title={timezoneFull(b.timezone)}
                                  data-testid={`shift-tz-${s.id}`}
                                >
                                  <Globe2 size={9} className="text-zinc-400 shrink-0" /> {tz.full}
                                </p>
                              ) : null;
                            })()}
                            <div className="mt-1">
                              {renderValidationControl(s)}
                            </div>
                            <p className="text-sm font-medium text-zinc-900 mt-0.5 truncate">{u?.name || "—"}</p>
                            {cat && (
                              <p className="text-[10px] font-semibold uppercase tracking-wider truncate" style={{ color: cat.color }} data-testid={`shift-cat-label-${s.id}`}>
                                {cat.name}
                              </p>
                            )}
                            {s.role_label && <p className="text-[11px] text-zinc-500 truncate">{s.role_label}</p>}
                            {site && (
                              <p className="text-[11px] text-zinc-600 truncate mt-0.5 flex items-center gap-1">
                                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: site.color }} />
                                {site.name}
                              </p>
                            )}
                            {Array.isArray(s.breaks) && s.breaks.length > 0 && (
                              <p
                                className="text-[10px] text-amber-700 truncate mt-0.5 font-mono"
                                title={formatBreaksLabel(s.breaks)}
                                data-testid={`shift-breaks-label-${s.id}`}
                              >
                                ⏸ {s.breaks.length === 1
                                  ? `Pause ${s.breaks[0].start_time}-${s.breaks[0].end_time}`
                                  : `${s.breaks.length} pauses planifiées`}
                              </p>
                            )}
                            <button
                              type="button"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                setDetailsShift(s);
                                setDetailsOpen(true);
                              }}
                              className="mt-1.5 w-full inline-flex items-center justify-center gap-1 text-[10px] px-2 py-1 rounded-sm bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-700"
                              data-testid={`planning-consult-${s.id}`}
                            >
                              <Eye size={10} /> Consulter
                            </button>
                            {canEdit && !locked && (
                              <div className="mt-1 flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={(ev) => { ev.stopPropagation(); openEditShift(s); }}
                                  className="flex-1 inline-flex items-center justify-center gap-1 text-[10px] px-2 py-1 rounded-sm bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-700"
                                  data-testid={`planning-edit-${s.id}`}
                                >
                                  <Pencil size={10} /> Modifier
                                </button>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      onClick={(ev) => ev.stopPropagation()}
                                      className="inline-flex items-center justify-center px-1.5 py-1 rounded-sm bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-600"
                                      aria-label="Plus d'actions"
                                      data-testid={`planning-shift-more-${s.id}`}
                                    >
                                      <MoreVertical size={11} />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => setDupDialog({ open: true, shift: s })}
                                      data-testid={`planning-shift-duplicate-${s.id}`}
                                    >
                                      <Copy size={12} className="mr-2" /> Dupliquer / Récurrence
                                    </DropdownMenuItem>
                                    {s?.absence_status !== "absent" && !(s?.clock_in_time || s?.clocked_in_at || s?.pointage_clock_in) && (
                                      <DropdownMenuItem
                                        onClick={() => manualClockIn(s.id)}
                                        data-testid={`planning-shift-clock-in-${s.id}`}
                                      >
                                        <LogIn size={12} className="mr-2 text-emerald-600" /> Valider le pointage
                                      </DropdownMenuItem>
                                    )}
                                    {s?.absence_status !== "absent" && (
                                      <DropdownMenuItem
                                        onClick={() => markAbsent(s.id)}
                                        className="text-red-700 focus:text-red-800"
                                        data-testid={`planning-shift-mark-absent-${s.id}`}
                                      >
                                        <UserX size={12} className="mr-2 text-red-600" /> Marquer en absence
                                      </DropdownMenuItem>
                                    )}
                                    {s?.absence_status === "absent" && (
                                      <DropdownMenuItem
                                        onClick={() => unmarkAbsent(s.id)}
                                        data-testid={`planning-shift-unmark-absent-${s.id}`}
                                      >
                                        <RotateCcw size={12} className="mr-2 text-zinc-600" /> Annuler l&apos;absence
                                      </DropdownMenuItem>
                                    )}
                                    {s?.absence_status !== "absent" && s?.unavailability_status !== "unavailable" && (
                                      <DropdownMenuItem
                                        onClick={() => markUnavailable(s.id)}
                                        className="text-amber-700 focus:text-amber-800"
                                        data-testid={`planning-shift-mark-unavailable-${s.id}`}
                                      >
                                        <BedDouble size={12} className="mr-2 text-amber-600" /> Marquer en indisponibilité
                                      </DropdownMenuItem>
                                    )}
                                    {s?.unavailability_status === "unavailable" && (
                                      <DropdownMenuItem
                                        onClick={() => unmarkUnavailable(s.id)}
                                        data-testid={`planning-shift-unmark-unavailable-${s.id}`}
                                      >
                                        <RotateCcw size={12} className="mr-2 text-zinc-600" /> Annuler l&apos;indisponibilité
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem
                                      onClick={() => remove(s.id)}
                                      className="text-red-600 focus:text-red-700"
                                      data-testid={`planning-shift-delete-${s.id}`}
                                    >
                                      <Trash2 size={12} className="mr-2" /> Supprimer
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            )}
                            {locked && (
                              <button
                                onClick={() => openChangeRequest(s)}
                                className="opacity-0 group-hover:opacity-100 text-amber-700 text-[10px] mt-1 flex items-center gap-1"
                                data-testid={`request-change-shift-${s.id}`}
                              >
                                <MessageSquare size={10} /> Demander une modification
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <HorizontalScrollContainer testId="planning-month-scroll" className="bg-white border border-zinc-200 rounded-sm">
        <div className="min-w-[900px]">
          <div className="grid grid-cols-7 border-b border-zinc-200">
            {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((wd) => (
              <div key={wd} className="p-2 border-r border-zinc-200 last:border-r-0">
                <p className="label-uppercase text-center">{wd}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((d, i) => {
              const ds = fmt(d);
              const dayShifts = filteredShifts.filter((s) => s.date === ds);
              const isCurrentMonth = d.getMonth() === cursor.getMonth();
              const isDragOver = dragOverDate === ds;
              const nwdEntries = nwdByDate[ds] || [];
              const isNonWorking = nwdEntries.length > 0;
              return (
                <div
                  key={i}
                  onDragOver={(e) => handleDragOver(e, ds)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, ds)}
                  className={`group p-2 border-r border-b border-zinc-200 last:border-r-0 space-y-1.5 min-h-[120px] ${isNonWorking ? "" : !isCurrentMonth ? "bg-zinc-50/60" : ""} ${isDragOver && canEdit ? "bg-blue-50 ring-2 ring-blue-300 ring-inset" : ""}`}
                  style={isNonWorking ? {
                    background: `repeating-linear-gradient(45deg, ${(nwdEntries[0].color || "#DC2626")}18 0 8px, ${(nwdEntries[0].color || "#DC2626")}0A 8px 16px)`,
                  } : undefined}
                  data-testid={`day-cell-${ds}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className={`text-[11px] font-semibold ${isCurrentMonth ? "text-zinc-700" : "text-zinc-300"}`}>{d.getDate()}</p>
                    {canEdit && isCurrentMonth && !isNonWorking && (
                      <button
                        type="button"
                        onClick={() => openNewShiftForDay(ds)}
                        aria-label={`Créer un shift le ${ds}`}
                        title="Créer un shift ce jour"
                        data-testid={`day-add-shift-${ds}`}
                        className="h-5 w-5 flex items-center justify-center rounded-sm text-zinc-400 hover:bg-[#0055FF] hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Plus size={11} />
                      </button>
                    )}
                  </div>
                  {isNonWorking && (
                    <NonWorkingDayBanner
                      entries={nwdEntries}
                      compact
                      showBrands={false}
                      testIdPrefix="planning-month-nwd"
                    />
                  )}
                  {dayShifts.map((s) => {
                    const u = users.find((x) => x.id === s.collaborateur_id);
                    const b = brands.find((x) => x.id === s.brand_id);
                    const cat = shiftCategories.find((x) => x.id === s.shift_category_id);
                    const locked = isLockedForManager(s);
                    return (
                      <div
                        key={s.id}
                        draggable={canEdit && !locked}
                        onDragStart={(e) => handleDragStart(e, s)}
                        className={`group p-2 border rounded-sm ${validationCardClasses(validationStatusOf(s))} ${canEdit && !locked ? "cursor-grab active:cursor-grabbing" : "cursor-default"}`}
                        style={{
                          borderLeftWidth: 3,
                          borderLeftColor: cat?.color || b?.color || "#0055FF",
                        }}
                        data-testid={`planning-shift-${s.id}`}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <p className="text-[11px] font-semibold text-blue-900">{s.start_time}–{s.end_time}</p>
                          {canEdit && !locked && <GripVertical size={10} className="text-zinc-300 mt-0.5" />}
                          {locked && <Lock size={10} className="text-amber-600 mt-0.5" data-testid={`shift-locked-${s.id}`} />}
                        </div>
                        {b?.timezone && (() => {
                          const tz = formatTimezoneLabel(b.timezone);
                          return tz.short ? (
                            <p
                              className="mt-0.5 text-[9px] text-zinc-500 font-mono truncate"
                              title={timezoneFull(b.timezone)}
                              data-testid={`shift-tz-${s.id}`}
                            >
                              {tz.short}
                            </p>
                          ) : null;
                        })()}
                        <div className="mt-1">
                          {renderValidationControl(s, { compact: true })}
                        </div>
                        <p className="text-xs font-medium text-zinc-900 mt-0.5 truncate">{u?.name || "—"}</p>
                        {cat && (
                          <p className="text-[10px] font-semibold uppercase tracking-wider truncate" style={{ color: cat.color }}>
                            {cat.name}
                          </p>
                        )}
                        {Array.isArray(s.breaks) && s.breaks.length > 0 && (
                          <p
                            className="text-[10px] text-amber-700 truncate font-mono"
                            title={formatBreaksLabel(s.breaks)}
                            data-testid={`shift-breaks-label-${s.id}`}
                          >
                            ⏸ {s.breaks.length === 1
                              ? `${s.breaks[0].start_time}-${s.breaks[0].end_time}`
                              : `${s.breaks.length} pauses`}
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setDetailsShift(s);
                            setDetailsOpen(true);
                          }}
                          className="mt-1.5 w-full inline-flex items-center justify-center gap-1 text-[10px] px-2 py-1 rounded-sm bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-700"
                          data-testid={`planning-consult-${s.id}`}
                        >
                          <Eye size={10} /> Consulter
                        </button>
                        {canEdit && !locked && (
                          <div className="mt-1 flex items-center gap-1">
                            <button
                              type="button"
                              onClick={(ev) => { ev.stopPropagation(); openEditShift(s); }}
                              className="flex-1 inline-flex items-center justify-center gap-1 text-[10px] px-1.5 py-1 rounded-sm bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-700"
                              data-testid={`planning-edit-${s.id}`}
                            >
                              <Pencil size={10} /> Modifier
                            </button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  onClick={(ev) => ev.stopPropagation()}
                                  className="inline-flex items-center justify-center px-1.5 py-1 rounded-sm bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-600"
                                  aria-label="Plus d'actions"
                                  data-testid={`planning-shift-more-${s.id}`}
                                >
                                  <MoreVertical size={10} />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => setDupDialog({ open: true, shift: s })}
                                  data-testid={`planning-shift-duplicate-${s.id}`}
                                >
                                  <Copy size={12} className="mr-2" /> Dupliquer / Récurrence
                                </DropdownMenuItem>
                                {s?.absence_status !== "absent" && !(s?.clock_in_time || s?.clocked_in_at || s?.pointage_clock_in) && (
                                  <DropdownMenuItem
                                    onClick={() => manualClockIn(s.id)}
                                    data-testid={`planning-shift-clock-in-${s.id}`}
                                  >
                                    <LogIn size={12} className="mr-2 text-emerald-600" /> Valider le pointage
                                  </DropdownMenuItem>
                                )}
                                {s?.absence_status !== "absent" && (
                                  <DropdownMenuItem
                                    onClick={() => markAbsent(s.id)}
                                    className="text-red-700 focus:text-red-800"
                                    data-testid={`planning-shift-mark-absent-${s.id}`}
                                  >
                                    <UserX size={12} className="mr-2 text-red-600" /> Marquer en absence
                                  </DropdownMenuItem>
                                )}
                                {s?.absence_status === "absent" && (
                                  <DropdownMenuItem
                                    onClick={() => unmarkAbsent(s.id)}
                                    data-testid={`planning-shift-unmark-absent-${s.id}`}
                                  >
                                    <RotateCcw size={12} className="mr-2 text-zinc-600" /> Annuler l&apos;absence
                                  </DropdownMenuItem>
                                )}
                                {s?.absence_status !== "absent" && s?.unavailability_status !== "unavailable" && (
                                  <DropdownMenuItem
                                    onClick={() => markUnavailable(s.id)}
                                    className="text-amber-700 focus:text-amber-800"
                                    data-testid={`planning-shift-mark-unavailable-${s.id}`}
                                  >
                                    <BedDouble size={12} className="mr-2 text-amber-600" /> Marquer en indisponibilité
                                  </DropdownMenuItem>
                                )}
                                {s?.unavailability_status === "unavailable" && (
                                  <DropdownMenuItem
                                    onClick={() => unmarkUnavailable(s.id)}
                                    data-testid={`planning-shift-unmark-unavailable-${s.id}`}
                                  >
                                    <RotateCcw size={12} className="mr-2 text-zinc-600" /> Annuler l&apos;indisponibilité
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() => remove(s.id)}
                                  className="text-red-600 focus:text-red-700"
                                  data-testid={`planning-shift-delete-${s.id}`}
                                >
                                  <Trash2 size={12} className="mr-2" /> Supprimer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </HorizontalScrollContainer>
      )}

      <ShiftDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        shift={detailsShift}
        brand={detailsShift ? brands.find((b) => b.id === detailsShift.brand_id) : null}
        site={detailsShift ? sites.find((s) => s.id === detailsShift.site_id) : null}
        category={detailsShift ? shiftCategories.find((c) => c.id === detailsShift.shift_category_id) : null}
        collab={detailsShift ? users.find((u) => u.id === detailsShift.collaborateur_id) : null}
      />
      <DuplicateRecurrenceDialog
        open={dupDialog.open}
        onOpenChange={(v) => setDupDialog((s) => ({ ...s, open: v }))}
        kind="shift"
        source={dupDialog.shift ? {
          id: dupDialog.shift.id,
          date: dupDialog.shift.date,
          start_time: dupDialog.shift.start_time,
          end_time: dupDialog.shift.end_time,
          label: (users.find((u) => u.id === dupDialog.shift.collaborateur_id)?.name) || "Shift",
        } : null}
        onSuccess={() => load()}
        onViewConflictShift={viewConflictShift}
      />
    </div>
  );
}
